import React, { useEffect } from 'react';
import { X, Trophy, Target, UserPlus } from 'lucide-react';
import type { PodiumEntry } from '../../shared/protocol';

interface Props {
  /** Resultado de quien juega como invitado, para enseñarle qué se guardaría. */
  entry: PodiumEntry;
  placement: number;
  totalPlayers: number;
  onRegister: () => void;
  onLogin: () => void;
  onClose: () => void;
}

/**
 * Aviso al terminar una partida sin cuenta: "¿guardar tu resultado?".
 *
 * Sale sobre el podio, no en lugar de él: primero se ve cómo te fue y después
 * se ofrece conservarlo. Aceptar abre el registro (o la entrada, para quien ya
 * tenga cuenta) y, al completarlo, la partida pasa al historial de la cuenta.
 */
export const SaveResultModal: React.FC<Props> = ({
  entry,
  placement,
  totalPlayers,
  onRegister,
  onLogin,
  onClose,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/85 backdrop-blur-sm flex items-start sm:items-center-safe justify-center p-4 overflow-y-auto"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-result-title"
    >
      <div className="surface-raised bg-ink-900 w-full max-w-[26rem] my-auto animate-pop">
        <header className="flex items-start gap-3 px-6 pt-6 pb-5">
          <Trophy className="w-8 h-8 text-ember-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <h2
              id="save-result-title"
              className="font-display font-semibold text-[1.0625rem] text-ink-50"
            >
              ¿Quieres guardar tu resultado?
            </h2>
            <p className="text-ink-400 text-[0.8125rem] leading-snug mt-0.5">
              Juegas como invitado: si cierras el navegador, esta partida se pierde. Crea una
              cuenta gratis y quedará en tu historial.
            </p>
          </div>
          <button
            onClick={onClose}
            className="-mt-1 -mr-2 p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </header>

        <div className="px-6 pb-6 space-y-4">
          <dl className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-ink-800 px-2 py-3">
              <dt className="text-ink-500 text-[0.6875rem] uppercase tracking-wider">Puesto</dt>
              <dd className="font-display font-bold text-ink-50 text-lg">
                {placement}.º<span className="text-ink-500 text-xs font-normal"> / {totalPlayers}</span>
              </dd>
            </div>
            <div className="rounded-lg bg-ink-800 px-2 py-3">
              <dt className="text-ink-500 text-[0.6875rem] uppercase tracking-wider">Puntos</dt>
              <dd className="font-display font-bold text-ink-50 text-lg">
                {entry.score.toLocaleString()}
              </dd>
            </div>
            <div className="rounded-lg bg-ink-800 px-2 py-3">
              <dt className="text-ink-500 text-[0.6875rem] uppercase tracking-wider flex items-center justify-center gap-1">
                <Target className="w-3 h-3" />
                Acierto
              </dt>
              <dd className="font-display font-bold text-ink-50 text-lg">
                {Math.round(entry.accuracy * 100)}%
              </dd>
            </div>
          </dl>

          <button
            onClick={onRegister}
            autoFocus
            className="w-full flex items-center justify-center gap-2 h-11 rounded-lg font-semibold
                       bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Crear cuenta y guardar
          </button>

          <div className="flex items-center justify-between text-[0.8125rem]">
            <button onClick={onLogin} className="text-sky-400 hover:text-sky-300">
              Ya tengo cuenta
            </button>
            <button onClick={onClose} className="text-ink-400 hover:text-ink-100">
              Ahora no
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
